import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { performanceApi } from '@/api/performance'
import type { EmployeePerformanceRow } from '@/types/performance'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { ArrowDown, ArrowUp, ArrowUpDown, Loader2, Trophy } from 'lucide-react'
import { DateRangePicker } from '@/components/DateRangePicker'

const formatMoney = (v: number) => `¥${(v ?? 0).toFixed(2)}`

type SortField = keyof Pick<
  EmployeePerformanceRow,
  'user_name' | 'dept_name' | 'total_promotion' | 'new_promotion' | 'vip_promotion' | 'new_count' | 'vip_count' | 'total_count' | 'unit_price'
>
type SortDirection = 'asc' | 'desc'

const sortFields: { field: SortField; label: string; numeric: boolean }[] = [
  { field: 'dept_name', label: '单位', numeric: false },
  { field: 'user_name', label: '员工', numeric: false },
  { field: 'total_promotion', label: '总业绩', numeric: true },
  { field: 'new_promotion', label: '新客户业绩', numeric: true },
  { field: 'vip_promotion', label: '复购业绩', numeric: true },
  { field: 'new_count', label: '新客户订单数', numeric: true },
  { field: 'vip_count', label: '复购订单数', numeric: true },
  { field: 'total_count', label: '总订单数', numeric: true },
  { field: 'unit_price', label: '客单价', numeric: true },
]

export function EmployeePerformancePage() {
  const today = new Date()
  const firstDay = new Date(today.getFullYear(), today.getMonth(), 1)
  const defaultRange = {
    start_date: firstDay.toISOString().slice(0, 10),
    end_date: today.toISOString().slice(0, 10),
  }
  const [dateRange, setDateRange] = useState(defaultRange)
  const [queryDateRange, setQueryDateRange] = useState(defaultRange)

  const { data, isLoading } = useQuery({
    queryKey: ['employee-report', queryDateRange.start_date, queryDateRange.end_date],
    queryFn: () => performanceApi.getEmployeeReport({
      start_date: queryDateRange.start_date,
      end_date: queryDateRange.end_date,
    }),
  })

  const handleSearch = () => {
    setQueryDateRange(dateRange)
  }

  const [sortField, setSortField] = useState<SortField>('total_promotion')
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc')

  const handleSort = (field: SortField) => {
    if (field === sortField) {
      setSortDirection((d) => (d === 'asc' ? 'desc' : 'asc'))
    } else {
      setSortField(field)
      setSortDirection('desc')
    }
  }

  const list = useMemo(() => {
    const rows = data?.list ?? []
    const config = sortFields.find((f) => f.field === sortField)
    const factor = sortDirection === 'asc' ? 1 : -1
    return [...rows].sort((a, b) => {
      const av = a[sortField]
      const bv = b[sortField]
      if (config?.numeric) {
        return ((av as number) - (bv as number)) * factor
      }
      return String(av).localeCompare(String(bv), 'zh-Hans-CN') * factor
    })
  }, [data?.list, sortField, sortDirection])

  const SortIcon = ({ field }: { field: SortField }) => {
    if (field !== sortField) return <ArrowUpDown className="ml-1 inline h-3.5 w-3.5 opacity-40" />
    return sortDirection === 'asc'
      ? <ArrowUp className="ml-1 inline h-3.5 w-3.5" />
      : <ArrowDown className="ml-1 inline h-3.5 w-3.5" />
  }

  const total = data?.total

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold flex items-center gap-2">
          <Trophy className="h-8 w-8" />
          员工业绩
        </h1>
        <p className="text-muted-foreground mt-1">按员工聚合的业绩数据</p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>筛选条件</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-end gap-4">
            <div className="space-y-1 w-56">
              <Label className="text-sm">日期范围</Label>
              <DateRangePicker
                value={dateRange}
                onChange={(v) => setDateRange({ start_date: v.start_date ?? '', end_date: v.end_date ?? '' })}
                className="w-full h-9"
              />
            </div>
            <Button onClick={handleSearch} disabled={isLoading}>
              {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              查询
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                {sortFields.map(({ field, label }, index) => (
                  <TableHead
                    key={field}
                    className={index >= 2 ? 'text-right' : undefined}
                  >
                    <button
                      type="button"
                      className="inline-flex items-center hover:text-foreground"
                      onClick={() => handleSort(field)}
                      aria-label={`按${label}排序`}
                    >
                      {label}
                      <SortIcon field={field} />
                    </button>
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {total && (
                <TableRow className="bg-muted/50">
                  <TableCell colSpan={2} className="font-bold">汇总</TableCell>
                  <TableCell className="text-right font-bold">{formatMoney(total.total_promotion)}</TableCell>
                  <TableCell className="text-right">{formatMoney(total.new_promotion)}</TableCell>
                  <TableCell className="text-right">{formatMoney(total.vip_promotion)}</TableCell>
                  <TableCell className="text-right">{total.new_count}</TableCell>
                  <TableCell className="text-right">{total.vip_count}</TableCell>
                  <TableCell className="text-right">{total.total_count}</TableCell>
                  <TableCell className="text-right">{formatMoney(total.unit_price)}</TableCell>
                </TableRow>
              )}
              {list.map((row: EmployeePerformanceRow) => (
                <TableRow key={row.user_id}>
                  <TableCell>{row.dept_name}</TableCell>
                  <TableCell className="font-medium">{row.user_name}</TableCell>
                  <TableCell className="text-right font-bold">{formatMoney(row.total_promotion)}</TableCell>
                  <TableCell className="text-right">{formatMoney(row.new_promotion)}</TableCell>
                  <TableCell className="text-right">{formatMoney(row.vip_promotion)}</TableCell>
                  <TableCell className="text-right">{row.new_count}</TableCell>
                  <TableCell className="text-right">{row.vip_count}</TableCell>
                  <TableCell className="text-right">{row.total_count}</TableCell>
                  <TableCell className="text-right">{formatMoney(row.unit_price)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {isLoading && (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          )}
          {!isLoading && list.length === 0 && (
            <p className="text-center text-muted-foreground py-8">暂无数据</p>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
